// Cómo se va a ver el post en el feed — ADR-2026-10-06-el-calendario-se-ve-como-instagram.
//
// Buffer y Later lo ponen al lado del editor (Mobbin): lo que más se equivoca al armar un
// post es la primera línea del texto, que es lo único que se lee antes del "… más", y la
// portada, que es la que frena el scroll. Esto muestra las dos tal como las corta Instagram.
// No es una captura de Instagram: es un mock con su estructura, sin su marca.

import { useEffect, useState } from 'react';
import { Bookmark, ChevronLeft, ChevronRight, Heart, ImageOff, MessageCircle, Play, Send } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import type { MedioDePost } from './post';

/** Instagram corta el pie a dos renglones en el feed; ~125 caracteres en un teléfono. */
const CORTE_DEL_TEXTO = 125;

export function VistaPreviaDeInstagram({
  medios,
  miniaturas,
  texto,
  cuando,
  marca,
  logo,
}: {
  medios: readonly MedioDePost[];
  miniaturas: readonly string[];
  texto: string;
  cuando: Date | null;
  marca: string;
  logo: string | null;
}) {
  const [actual, setActual] = useState(0);
  const [completo, setCompleto] = useState(false);
  const total = medios.length;

  // Se sacó una pieza o se cambió de post: que no quede apuntando afuera.
  useEffect(() => {
    if (actual > 0 && actual >= total) setActual(Math.max(0, total - 1));
  }, [actual, total]);

  const usuario = marca.trim().toLowerCase().replace(/\s+/g, '') || 'tumarca';
  const limpio = texto.trim();
  const corto = limpio.length > CORTE_DEL_TEXTO && !completo;
  const visible = corto ? `${limpio.slice(0, CORTE_DEL_TEXTO).trimEnd()}` : limpio;
  const medio = medios[actual];
  const url = miniaturas[actual] ?? '';

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="flex items-center gap-2.5 py-2.5 px-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 p-[2px]">
          <span className="grid h-full w-full place-items-center overflow-hidden rounded-full bg-white text-[11px] font-semibold text-brand-black">
            {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : usuario[0]?.toUpperCase()}
          </span>
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-brand-black">{usuario}</span>
        <span aria-hidden className="text-lg leading-none text-neutral-400">···</span>
      </div>

      {/* 4:5, el formato vertical del feed: el que ocupa más pantalla. */}
      <div className="relative aspect-[4/5] bg-neutral-100">
        {total === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-neutral-400">
            <ImageOff className="w-7 h-7" />
            <span className="text-xs">Sumá una foto o un video</span>
          </div>
        ) : url ? (
          <CachedImg src={url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full place-items-center text-neutral-400">
            <Play className="w-8 h-8" />
          </div>
        )}

        {medio?.esVideo && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-black/60 py-1 px-2 text-[11px] font-medium text-white">
            {/* Un video solo es un reel; adentro de un carrusel es una pieza más. */}
            <Play className="w-3 h-3" /> {total > 1 ? 'Video' : 'Reel'}
          </span>
        )}
        {total > 1 && (
          <>
            <span className="absolute right-2.5 top-2.5 rounded-full bg-black/60 py-0.5 px-2 text-[11px] font-medium text-white">
              {actual + 1}/{total}
            </span>
            {actual > 0 && (
              <button
                type="button"
                onClick={() => setActual((i) => i - 1)}
                aria-label="Pieza anterior"
                className="absolute left-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-brand-black shadow"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {actual < total - 1 && (
              <button
                type="button"
                onClick={() => setActual((i) => i + 1)}
                aria-label="Pieza siguiente"
                className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-brand-black shadow"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </div>

      <div className="py-2.5 px-3">
        <div className="flex items-center gap-3.5 text-brand-black">
          <Heart className="w-5 h-5" />
          <MessageCircle className="w-5 h-5" />
          <Send className="w-5 h-5" />
          {total > 1 && (
            <span className="mx-auto flex gap-1">
              {medios.map((m, i) => (
                <span
                  key={m.id}
                  className={`h-1.5 w-1.5 rounded-full ${i === actual ? 'bg-brand-primary' : 'bg-neutral-300'}`}
                />
              ))}
            </span>
          )}
          <Bookmark className={`w-5 h-5 ${total > 1 ? '' : 'ml-auto'}`} />
        </div>

        <p className="mt-2 whitespace-pre-line break-words text-[13px] leading-snug text-brand-black">
          <span className="font-semibold">{usuario}</span>{' '}
          {limpio ? (
            <>
              {visible}
              {corto && (
                <button type="button" onClick={() => setCompleto(true)} className="text-neutral-400">
                  … más
                </button>
              )}
            </>
          ) : (
            <span className="text-neutral-400">Acá va la descripción.</span>
          )}
        </p>
        <p className="mt-1.5 text-[10px] uppercase tracking-wide text-neutral-400">
          {cuando
            ? cuando.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })
            : 'Sin fecha'}
        </p>
      </div>
    </div>
  );
}
